const BLOCKED_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'canvas',
  'video',
  'audio',
  'source',
  'track',
  'noscript',
]);

const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'span',
  'strong',
  'strike',
  's',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

const SAFE_STYLE_PROPERTIES = ['color', 'background-color'];
const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function normalizePotentialHtmlDescription(value: string): string {
  let normalized = value.trim();
  if (!normalized) {
    return normalized;
  }

  for (let iteration = 0; iteration < 3; iteration += 1) {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = normalized;
    const decoded = decoder.value.trim();

    if (!decoded) {
      return '';
    }

    if (decoded === normalized) {
      return decoded;
    }

    normalized = decoded;
  }

  return normalized;
}

function sanitizeUrl(value: string, allowDataImage = false): string | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.startsWith('//') || /^javascript:/i.test(trimmed) || /^vbscript:/i.test(trimmed)) {
    return null;
  }

  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('./') || trimmed.startsWith('../')) {
    return trimmed;
  }

  if (allowDataImage && /^data:image\/(png|jpe?g|gif|webp|bmp);/i.test(trimmed)) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed, window.location.origin);
    return SAFE_LINK_PROTOCOLS.has(parsed.protocol) ? trimmed : null;
  } catch {
    return null;
  }
}

function sanitizeStyleValue(property: string, value: string): string {
  const probe = document.createElement('span');
  probe.style.setProperty(property, value);
  return probe.style.getPropertyValue(property).trim();
}

function sanitizeStyleAttribute(value: string): string {
  const safeStyles: string[] = [];
  const declarations = value.split(';');

  for (const declaration of declarations) {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex < 0) {
      continue;
    }

    const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
    const rawValue = declaration.slice(separatorIndex + 1).trim();
    if (!SAFE_STYLE_PROPERTIES.includes(property) || !rawValue) {
      continue;
    }

    const sanitizedValue = sanitizeStyleValue(property, rawValue);
    if (sanitizedValue) {
      safeStyles.push(`${property}: ${sanitizedValue};`);
    }
  }

  return safeStyles.join(' ');
}

function sanitizeElementAttributes(sourceElement: Element, targetElement: Element): boolean {
  for (const attr of Array.from(sourceElement.attributes)) {
    const attrName = attr.name.toLowerCase();
    const attrValue = attr.value;

    if (attrName === 'style') {
      const sanitizedStyle = sanitizeStyleAttribute(attrValue);
      if (sanitizedStyle) {
        targetElement.setAttribute('style', sanitizedStyle);
      }
      continue;
    }

    if (attrName === 'title' || attrName === 'aria-label' || attrName === 'aria-hidden') {
      targetElement.setAttribute(attrName, attrValue);
      continue;
    }

    if (targetElement.tagName.toLowerCase() === 'a' && attrName === 'href') {
      const sanitizedHref = sanitizeUrl(attrValue);
      if (sanitizedHref) {
        targetElement.setAttribute('href', sanitizedHref);
      }
      continue;
    }

    if (targetElement.tagName.toLowerCase() === 'a' && attrName === 'target') {
      const normalizedTarget = attrValue.trim().toLowerCase();
      if (['_blank', '_self', '_parent', '_top'].includes(normalizedTarget)) {
        targetElement.setAttribute('target', normalizedTarget);
      }
      continue;
    }

    if (targetElement.tagName.toLowerCase() === 'img' && attrName === 'src') {
      const sanitizedSrc = sanitizeUrl(attrValue, true);
      if (!sanitizedSrc) {
        return false;
      }

      targetElement.setAttribute('src', sanitizedSrc);
      continue;
    }

    if (targetElement.tagName.toLowerCase() === 'img' && (attrName === 'alt' || attrName === 'width' || attrName === 'height' || attrName === 'title')) {
      targetElement.setAttribute(attrName, attrValue);
      continue;
    }

    if ((targetElement.tagName.toLowerCase() === 'td' || targetElement.tagName.toLowerCase() === 'th') && (attrName === 'colspan' || attrName === 'rowspan')) {
      targetElement.setAttribute(attrName, attrValue);
    }
  }

  if (targetElement.tagName.toLowerCase() === 'a') {
    targetElement.setAttribute('rel', 'noopener noreferrer');
  }

  return true;
}

function sanitizeHtmlNode(node: Node): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent ?? '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const sourceElement = node as Element;
  const tagName = sourceElement.tagName.toLowerCase();

  if (BLOCKED_TAGS.has(tagName)) {
    return null;
  }

  if (!ALLOWED_TAGS.has(tagName)) {
    const fragment = document.createDocumentFragment();
    for (const childNode of Array.from(sourceElement.childNodes)) {
      const sanitizedChild = sanitizeHtmlNode(childNode);
      if (sanitizedChild) {
        fragment.appendChild(sanitizedChild);
      }
    }

    return fragment;
  }

  const targetElement = document.createElement(tagName);
  if (!sanitizeElementAttributes(sourceElement, targetElement)) {
    return null;
  }

  for (const childNode of Array.from(sourceElement.childNodes)) {
    const sanitizedChild = sanitizeHtmlNode(childNode);
    if (sanitizedChild) {
      targetElement.appendChild(sanitizedChild);
    }
  }

  if (tagName === 'img' && !targetElement.getAttribute('src')) {
    return null;
  }

  return targetElement;
}

export function sanitizeRichDescriptionHtml(description: string | null | undefined): string {
  const normalized = normalizePotentialHtmlDescription(description ?? '');
  if (!normalized) {
    return '';
  }

  const parser = new DOMParser();
  const sourceDocument = parser.parseFromString(normalized, 'text/html');
  const targetContainer = document.createElement('div');

  for (const node of Array.from(sourceDocument.body.childNodes)) {
    const sanitizedNode = sanitizeHtmlNode(node);
    if (sanitizedNode) {
      targetContainer.appendChild(sanitizedNode);
    }
  }

  return targetContainer.innerHTML.trim();
}

function extractPlainTextDescription(description: string): string {
  const normalized = sanitizeRichDescriptionHtml(description);
  if (!normalized) {
    return '';
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(normalized, 'text/html');

  return (document.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function containsHtmlMarkup(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

export function buildListItemDescriptionPreview(description: string | null | undefined): string {
  return extractPlainTextDescription(description ?? '');
}

export function buildRichDescriptionTooltipHtml(description: string | null | undefined): string | null {
  const normalized = sanitizeRichDescriptionHtml(description);
  if (!normalized) {
    return null;
  }

  if (!containsHtmlMarkup(normalized)) {
    return `<p>${escapeHtml(normalized)}</p>`;
  }

  return normalized;
}
